import { motion } from "motion/react";
import { type SimulatorInput, getDefaultInput, SCENARIO_PRESETS } from "./SimulatorEngine";

interface SimulatorFormProps {
  data: SimulatorInput;
  onChange: (data: SimulatorInput) => void;
}

function numberInput(value: number, onChange: (v: number) => void, suffix?: string, step?: number) {
  return (
    <div className="relative">
      <input
        type="text"
        inputMode="numeric"
        value={value > 0 ? value.toLocaleString() : (step !== undefined && step < 1 ? value.toString() : "")}
        onChange={(e) => {
          const raw = e.target.value.replace(/[^0-9.]/g, "");
          if (step !== undefined && step < 1) {
            const num = parseFloat(raw);
            onChange(isNaN(num) ? 0 : num);
          } else {
            onChange(raw ? parseInt(raw, 10) : 0);
          }
        }}
        className="w-full pl-4 pr-12 py-2.5 text-sm text-right border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors font-medium"
        placeholder="0"
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#8C8780] pointer-events-none">{suffix}</span>
      )}
    </div>
  );
}

function sliderInput(
  value: number,
  onChange: (v: number) => void,
  min: number,
  max: number,
  step: number,
  formatLabel: (v: number) => string,
  marks?: number[],
) {
  return (
    <div className="space-y-1">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 rounded-full appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, #C4956A 0%, #C4956A ${((value - min) / (max - min)) * 100}%, #E8E5E0 ${((value - min) / (max - min)) * 100}%, #E8E5E0 100%)`,
        }}
      />
      <style>{`
        input[type=range]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: white;
          border: 2px solid #C4956A;
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
          cursor: pointer;
          transition: all 0.15s;
        }
        input[type=range]::-webkit-slider-thumb:hover {
          border-color: #A0784C;
          transform: scale(1.1);
        }
        input[type=range]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: white;
          border: 2px solid #C4956A;
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
          cursor: pointer;
        }
      `}</style>
      <div className="flex justify-between text-[10px] text-[#B8B3AE]">
        <span>{formatLabel(min)}</span>
        {marks && marks.slice(1, -1).map((m, i) => (
          <span key={i} className="hidden sm:inline">{formatLabel(m)}</span>
        ))}
        <span>{formatLabel(max)}</span>
      </div>
    </div>
  );
}

function formatShortKRW(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 100000000) return sign + (abs / 100000000).toFixed(1) + "억";
  if (abs >= 10000) return sign + Math.round(abs / 10000) + "만";
  return n.toLocaleString();
}

export default function SimulatorForm({ data, onChange }: SimulatorFormProps) {
  const update = (patch: Partial<SimulatorInput>) => onChange({ ...data, ...patch });

  // 실시간 최대낙찰가
  const maxBidPrice = data.ltv < 1 ? Math.round(data.equity / (1 - data.ltv)) : 0;

  return (
    <div className="space-y-5">
      {/* 시나리오 프리셋 */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-4 md:p-5">
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-speed-up-line text-[#C4956A] text-sm"></i>
          <h3 className="text-xs font-bold text-[#1C1917]">빠른 시나리오 선택</h3>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {SCENARIO_PRESETS.map((preset) => {
            const isActive =
              data.ltv === preset.overrides.ltv &&
              data.bidRate === preset.overrides.bidRate &&
              data.saleRate === preset.overrides.saleRate;
            return (
              <motion.button
                key={preset.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => {
                  const def = getDefaultInput();
                  const merged = { ...data, ...preset.overrides };
                  // 계산된 maxBidPrice를 기준으로 appraisalValue 조정
                  if (preset.overrides.ltv) {
                    const newMaxBid = Math.round(data.equity / (1 - preset.overrides.ltv));
                    // 감정가를 최대낙찰가 / 낙찰가율로 역산
                    if (preset.overrides.bidRate && preset.overrides.bidRate > 0) {
                      merged.appraisalValue = Math.round(newMaxBid / preset.overrides.bidRate);
                    }
                  }
                  onChange(merged);
                }}
                className={`p-3 rounded-lg border transition-all cursor-pointer text-left ${
                  isActive
                    ? "border-[#C4956A] bg-[#C4956A]/5"
                    : "border-[#E8E5E0] hover:border-[#D1CCC2] bg-white"
                }`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <i className={`${preset.icon} text-sm ${isActive ? "text-[#C4956A]" : "text-[#8C8780]"}`}></i>
                  <span className={`text-xs font-bold ${isActive ? "text-[#C4956A]" : "text-[#1C1917]"}`}>
                    {preset.label}
                  </span>
                </div>
                <p className="text-[10px] text-[#B8B3AE] leading-tight">{preset.description}</p>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Section 1: 기본 투자 정보 */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-5 md:p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#1C1917] to-[#3A2E28] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">1</span>
          </div>
          <h3 className="text-sm font-bold text-[#1C1917]">기본 투자 정보</h3>
          <span className="ml-auto px-2 py-0.5 bg-amber-50 border border-amber-200 rounded text-[10px] font-semibold text-amber-700">
            최대 낙찰가 {formatShortKRW(maxBidPrice)}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">자기자본 (E)</label>
            {numberInput(data.equity, (v) => update({ equity: v }), "원")}
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">대출비율 / LTV</label>
            <div className="mb-1">
              {sliderInput(
                Math.round(data.ltv * 100),
                (v) => update({ ltv: v / 100 }),
                30, 95, 5,
                (v) => v + "%",
                [30, 50, 60, 70, 80, 95],
              )}
            </div>
            <div className="text-center text-sm font-bold text-[#C4956A]">{(data.ltv * 100).toFixed(0)}%</div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">대출금리 (r, 연%)</label>
            {numberInput(data.loanRate, (v) => update({ loanRate: v }), "%")}
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">보유예정기간 (m, 개월)</label>
            {numberInput(data.holdingMonths, (v) => update({ holdingMonths: v }), "개월")}
          </div>
        </div>
      </div>

      {/* Section 2: 물건 정보 */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-5 md:p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#1C1917] to-[#3A2E28] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">2</span>
          </div>
          <h3 className="text-sm font-bold text-[#1C1917]">물건 정보</h3>
        </div>
        <div className="space-y-5">
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">감정가</label>
            {numberInput(data.appraisalValue, (v) => update({ appraisalValue: v }), "원")}
          </div>

          {/* 낙찰가율 슬라이더 */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-[#5C5650]">낙찰가율</label>
              <span className="text-xs font-bold text-[#C4956A]">
                {(data.bidRate * 100).toFixed(0)}% → {formatShortKRW(Math.round(data.appraisalValue * data.bidRate))}
              </span>
            </div>
            {sliderInput(
              Math.round(data.bidRate * 100),
              (v) => update({ bidRate: v / 100 }),
              55, 100, 1,
              (v) => v + "%",
            )}
          </div>

          {/* 매도가율 슬라이더 */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-[#5C5650]">예상 매도가율</label>
              <span className="text-xs font-bold text-[#C4956A]">
                {(data.saleRate * 100).toFixed(0)}% → {formatShortKRW(Math.round(data.appraisalValue * data.saleRate))}
              </span>
            </div>
            {sliderInput(
              Math.round(data.saleRate * 100),
              (v) => update({ saleRate: v / 100 }),
              65, 110, 1,
              (v) => v + "%",
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#5C5650] mb-1.5">물건 종류</label>
              <div className="flex gap-1 flex-wrap">
                {["아파트", "다세대/연립", "오피스텔", "단독주택", "상가"].map((t) => (
                  <button
                    key={t}
                    onClick={() => update({ propertyType: t })}
                    className={`px-2.5 py-1.5 text-xs rounded-full border transition-all whitespace-nowrap cursor-pointer ${
                      data.propertyType === t
                        ? "bg-[#1C1917] text-white border-[#1C1917] font-medium"
                        : "bg-white text-[#5C5650] border-[#E8E5E0] hover:border-[#C4956A]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5C5650] mb-1.5">점유자 유형</label>
              <div className="flex gap-1 flex-wrap">
                {[
                  { value: "없음", label: "없음" },
                  { value: "채무자", label: "채무자" },
                  { value: "임차인(무권리)", label: "무권리" },
                  { value: "임차인(대항력)", label: "대항력" },
                  { value: "유치권자", label: "유치권" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => update({ occupantType: opt.value })}
                    className={`px-2 py-1.5 text-xs rounded-full border transition-all whitespace-nowrap cursor-pointer ${
                      data.occupantType === opt.value
                        ? "bg-[#1C1917] text-white border-[#1C1917] font-medium"
                        : "bg-white text-[#5C5650] border-[#E8E5E0] hover:border-[#C4956A]"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">최근 3개월 동종 거래 건수</label>
            {numberInput(data.recentTransactions, (v) => update({ recentTransactions: v }), "건")}
          </div>
        </div>
      </div>

      {/* Section 3: 필요경비 항목 */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-5 md:p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#1C1917] to-[#3A2E28] flex items-center justify-center">
              <span className="text-[10px] text-white font-bold">3</span>
            </div>
            <h3 className="text-sm font-bold text-[#1C1917]">필요경비 항목</h3>
          </div>
          <button
            onClick={() => {
              const def = getDefaultInput();
              update({
                acquisitionTaxRate: def.acquisitionTaxRate,
                legalCost: def.legalCost,
                evictionCost: def.evictionCost,
                repairCost: def.repairCost,
                cleanCost: def.cleanCost,
                brokerRate: def.brokerRate,
              });
            }}
            className="text-[10px] text-[#8C8780] hover:text-[#C4956A] transition-colors flex items-center gap-1 cursor-pointer"
          >
            <i className="ri-refresh-line"></i>
            기본값 복원
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">취득세율 (%)</label>
            {numberInput(Math.round(data.acquisitionTaxRate * 1000) / 10, (v) => update({ acquisitionTaxRate: v / 100 }), "%", 0.001)}
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">법무·등기비</label>
            {numberInput(data.legalCost, (v) => update({ legalCost: v }), "원")}
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">명도비</label>
            {numberInput(data.evictionCost, (v) => update({ evictionCost: v }), "원")}
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">수리비</label>
            {numberInput(data.repairCost, (v) => update({ repairCost: v }), "원")}
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">청소·폐기물비</label>
            {numberInput(data.cleanCost, (v) => update({ cleanCost: v }), "원")}
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">매도 중개수수료율 (%)</label>
            {numberInput(Math.round(data.brokerRate * 1000) / 10, (v) => update({ brokerRate: v / 100 }), "%", 0.001)}
          </div>
        </div>
      </div>

      {/* 전체 초기화 */}
      <motion.button
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => onChange(getDefaultInput())}
        className="w-full py-2.5 text-sm font-medium text-[#8C8780] border border-[#E8E5E0] rounded-lg hover:border-[#C4956A] hover:text-[#C4956A] transition-all cursor-pointer whitespace-nowrap"
      >
        <i className="ri-refresh-line mr-1.5"></i>
        전체 초기값으로 리셋
      </motion.button>
    </div>
  );
}